'use client';

import { c as _c } from "react/compiler-runtime";
import { useActiveWallet, useGetMarketChainMetadata } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type { JSX, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { UserIdentity } from '@/components/profile/user-identity';
import { TradingRestrictedNotice } from '@/components/trade/trading-restricted-notice';
import { AnimatedButton } from '@/components/ui/animated-button';
import { CheckboxField } from '@/components/ui/checkbox-field';
import { Icon } from '@/components/ui/icon';
import { Alert01Icon, ArrowDown01IconSolid, ArrowUp01IconSolid } from '@/components/ui/icon/glyphs';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { useOutcomesLimits } from '@/hooks/launchpad/use-outcomes-limits';
import { useTradingRestricted } from '@/hooks/trading/use-trading-restricted';
import { effectiveImageUrl, effectiveLabel, effectiveTicker, type SuggestionWire } from '../types';
interface CreatorReviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  marketPubkey: string;
  suggestion: SuggestionWire | null;
  onConfirm: (suggestion: SuggestionWire) => void;
  isPending: boolean;
}
export function CreatorReviewModal(t0) {
  const $ = _c(122);
  const {
    open,
    onOpenChange,
    marketPubkey,
    suggestion,
    onConfirm,
    isPending
  } = t0;
  const t = useTranslations("launchpad.suggestedOutcomes.creator");
  const tWizard = useTranslations("launchpad.suggestedOutcomes.wizard");
  const tErrors = useTranslations("launchpad.errors");
  const [disclaimerAcked, setDisclaimerAcked] = useState(false);
  const {
    activeWallet
  } = useActiveWallet();
  const tradingRestricted = useTradingRestricted();
  let t1;
  let t2;
  if ($[0] !== open) {
    t1 = () => {
      if (!open) {
        setDisclaimerAcked(false);
      }
    };
    t2 = [open];
    $[0] = open;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const {
    maxOutcomes
  } = useOutcomesLimits(marketPubkey);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["outcomeCount", "creator"];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== marketPubkey) {
    t4 = {
      pubkey: marketPubkey,
      fields: t3
    };
    $[4] = marketPubkey;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const chainMetadata = useGetMarketChainMetadata(t4);
  const marketOutcomeCount = chainMetadata.data?.outcomeCount ?? null;
  const marketCreator = chainMetadata.data?.creator ?? null;
  const wouldExceedCap = maxOutcomes !== null && marketOutcomeCount !== null && marketOutcomeCount + 1 > maxOutcomes;
  const viewerIsCreator = marketCreator !== null && activeWallet?.address !== undefined && activeWallet.address === marketCreator;
  if (suggestion === null) {
    return null;
  }
  let t5;
  if ($[6] !== suggestion) {
    t5 = effectiveLabel(suggestion);
    $[6] = suggestion;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const label = t5;
  let t6;
  if ($[8] !== suggestion) {
    t6 = effectiveTicker(suggestion);
    $[8] = suggestion;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const ticker = t6;
  let t7;
  if ($[10] !== suggestion) {
    t7 = effectiveImageUrl(suggestion);
    $[10] = suggestion;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const imageUrl = t7;
  let t8;
  if ($[12] !== suggestion.suggesterPubkey) {
    t8 = suggestion.suggesterPubkey.slice(0, 4);
    $[12] = suggestion.suggesterPubkey;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== suggestion.suggesterPubkey) {
    t9 = suggestion.suggesterPubkey.slice(-4);
    $[14] = suggestion.suggesterPubkey;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const truncatedPubkey = `${t8}…${t9}`;
  const suggester = suggestion.suggester;
  let t10;
  if ($[16] !== suggester.connectedProviders) {
    t10 = suggester.connectedProviders ?? [];
    $[16] = suggester.connectedProviders;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const connectedProviders = t10;
  let t11;
  if ($[18] !== onOpenChange) {
    t11 = () => onOpenChange(false);
    $[18] = onOpenChange;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t) {
    t12 = t("cancel");
    $[20] = t;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== isPending || $[23] !== t11 || $[24] !== t12) {
    t13 = <AnimatedButton type="button" variant="secondary" onClick={t11} disabled={isPending}>{t12}</AnimatedButton>;
    $[22] = isPending;
    $[23] = t11;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== onConfirm || $[27] !== suggestion || $[28] !== viewerIsCreator) {
    t14 = () => {
      if (viewerIsCreator) {
        onConfirm(suggestion);
      }
    };
    $[26] = onConfirm;
    $[27] = suggestion;
    $[28] = viewerIsCreator;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const t15 = isPending || !disclaimerAcked || wouldExceedCap || !viewerIsCreator || tradingRestricted;
  let t16;
  if ($[30] !== isPending || $[31] !== t) {
    t16 = isPending ? t("adding") : t("addToMarket");
    $[30] = isPending;
    $[31] = t;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== isPending || $[34] !== t14 || $[35] !== t15 || $[36] !== t16) {
    t17 = <AnimatedButton type="button" variant="primary" onClick={t14} disabled={t15} loading={isPending}>{t16}</AnimatedButton>;
    $[33] = isPending;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t13 || $[39] !== t17) {
    t18 = <div className="flex items-center justify-between gap-3">{t13}{t17}</div>;
    $[38] = t13;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  const footer = t18;
  let t19;
  if ($[41] !== t) {
    t19 = t("title");
    $[41] = t;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <TradingRestrictedNotice />;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== imageUrl || $[45] !== label) {
    t21 = <OptimizedImage variant="thumbnail" src={imageUrl} alt={label} size="size-14" sizes="56px" rounded="rounded-item" className="size-14 shrink-0" imageClassName="size-14 object-cover" />;
    $[44] = imageUrl;
    $[45] = label;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== ticker) {
    t22 = <div className="font-mono text-body font-bold text-foreground-primary">${ticker}</div>;
    $[47] = ticker;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== label) {
    t23 = <div className="truncate text-caption text-foreground-secondary">{label}</div>;
    $[49] = label;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t22 || $[52] !== t23) {
    t24 = <div className="min-w-0">{t22}{t23}</div>;
    $[51] = t22;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t21 || $[55] !== t24) {
    t25 = <div className="flex items-center gap-3">{t21}{t24}</div>;
    $[54] = t21;
    $[55] = t24;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== suggestion.reason) {
    t26 = suggestion.reason !== null && suggestion.reason.length > 0 ? <p className="border-t pt-3 text-body text-foreground-primary">“{suggestion.reason}”</p> : null;
    $[57] = suggestion.reason;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t) {
    t27 = t("suggestedByLabel");
    $[59] = t;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== t27) {
    t28 = <span className="shrink-0">{t27}</span>;
    $[61] = t27;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] !== connectedProviders || $[64] !== suggester) {
    t29 = {
      ...suggester,
      connectedProviders
    };
    $[63] = connectedProviders;
    $[64] = suggester;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] !== t29 || $[67] !== truncatedPubkey) {
    t30 = <UserIdentity user={t29} variant="with-avatar" size="sm" fallback={truncatedPubkey} />;
    $[66] = t29;
    $[67] = truncatedPubkey;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== t28 || $[70] !== t30) {
    t31 = <div className="flex min-w-0 items-center gap-2 text-body text-foreground-secondary">{t28}{t30}</div>;
    $[69] = t28;
    $[70] = t30;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <Icon icon={ArrowUp01IconSolid} className="size-4 text-feedback-success-content" aria-hidden="true" />;
    $[72] = t32;
  } else {
    t32 = $[72];
  }
  let t33;
  if ($[73] !== suggestion.upvotes) {
    t33 = <span className="flex items-center gap-1">{t32}<span className="tabular-nums text-foreground-primary">{suggestion.upvotes}</span></span>;
    $[73] = suggestion.upvotes;
    $[74] = t33;
  } else {
    t33 = $[74];
  }
  let t34;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <Icon icon={ArrowDown01IconSolid} className="size-4 text-feedback-error-content" aria-hidden="true" />;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  let t35;
  if ($[76] !== suggestion.downvotes) {
    t35 = <span className="flex items-center gap-1">{t34}<span className="tabular-nums text-foreground-primary">{suggestion.downvotes}</span></span>;
    $[76] = suggestion.downvotes;
    $[77] = t35;
  } else {
    t35 = $[77];
  }
  let t36;
  if ($[78] !== t33 || $[79] !== t35) {
    t36 = <div className="flex shrink-0 items-center gap-2 text-body text-foreground-secondary">{t33}{t35}</div>;
    $[78] = t33;
    $[79] = t35;
    $[80] = t36;
  } else {
    t36 = $[80];
  }
  let t37;
  if ($[81] !== t31 || $[82] !== t36) {
    t37 = <div className="flex items-center justify-between gap-3 border-t pt-3">{t31}{t36}</div>;
    $[81] = t31;
    $[82] = t36;
    $[83] = t37;
  } else {
    t37 = $[83];
  }
  let t38;
  if ($[84] !== t25 || $[85] !== t26 || $[86] !== t37) {
    t38 = <div className="flex flex-col gap-3 rounded-panel-row border bg-background-secondary p-4">{t25}{t26}{t37}</div>;
    $[84] = t25;
    $[85] = t26;
    $[86] = t37;
    $[87] = t38;
  } else {
    t38 = $[87];
  }
  let t39;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <Icon icon={Alert01Icon} className="size-5 shrink-0 text-feedback-warning-content" aria-hidden="true" />;
    $[88] = t39;
  } else {
    t39 = $[88];
  }
  let t40;
  if ($[89] !== tWizard) {
    t40 = tWizard("disclaimerHeading");
    $[89] = tWizard;
    $[90] = t40;
  } else {
    t40 = $[90];
  }
  let t41;
  if ($[91] !== t40) {
    t41 = <p className="text-body font-medium text-foreground-primary">{t40}</p>;
    $[91] = t40;
    $[92] = t41;
  } else {
    t41 = $[92];
  }
  let t42;
  if ($[93] !== tWizard) {
    t42 = tWizard("disclaimerBody");
    $[93] = tWizard;
    $[94] = t42;
  } else {
    t42 = $[94];
  }
  let t43;
  if ($[95] !== t42) {
    t43 = <p className="text-caption text-foreground-secondary">{t42}</p>;
    $[95] = t42;
    $[96] = t43;
  } else {
    t43 = $[96];
  }
  let t44;
  if ($[97] !== marketOutcomeCount || $[98] !== maxOutcomes || $[99] !== tErrors || $[100] !== tWizard || $[101] !== wouldExceedCap) {
    t44 = maxOutcomes !== null && marketOutcomeCount !== null ? <p className={wouldExceedCap ? "text-caption font-medium text-feedback-error-content" : "text-caption text-foreground-tertiary"}>{wouldExceedCap ? tErrors("marketAtCapacity", {
        max: maxOutcomes
      }) : tWizard("marketCapacity", {
        current: marketOutcomeCount,
        max: maxOutcomes
      })}</p> : null;
    $[97] = marketOutcomeCount;
    $[98] = maxOutcomes;
    $[99] = tErrors;
    $[100] = tWizard;
    $[101] = wouldExceedCap;
    $[102] = t44;
  } else {
    t44 = $[102];
  }
  let t45;
  if ($[103] !== t41 || $[104] !== t43 || $[105] !== t44) {
    t45 = <div className="flex items-start gap-3 rounded-panel-row border border-feedback-warning-border/40 bg-feedback-warning-accent/10 p-4">{t39}<div className="flex flex-col gap-1">{t41}{t43}{t44}</div></div>;
    $[103] = t41;
    $[104] = t43;
    $[105] = t44;
    $[106] = t45;
  } else {
    t45 = $[106];
  }
  let t46;
  if ($[107] !== tWizard) {
    t46 = tWizard("disclaimerAcknowledge");
    $[107] = tWizard;
    $[108] = t46;
  } else {
    t46 = $[108];
  }
  let t47;
  if ($[109] !== disclaimerAcked || $[110] !== t46) {
    t47 = <CheckboxField label={t46} checked={disclaimerAcked} onCheckedChange={setDisclaimerAcked} required={true} />;
    $[109] = disclaimerAcked;
    $[110] = t46;
    $[111] = t47;
  } else {
    t47 = $[111];
  }
  let t48;
  if ($[112] !== t38 || $[113] !== t45 || $[114] !== t47) {
    t48 = <div className="flex flex-col gap-4">{t20}{t38}{t45}{t47}</div>;
    $[112] = t38;
    $[113] = t45;
    $[114] = t47;
    $[115] = t48;
  } else {
    t48 = $[115];
  }
  let t49;
  if ($[116] !== footer || $[117] !== onOpenChange || $[118] !== open || $[119] !== t19 || $[120] !== t48) {
    t49 = <SilkSheet open={open} onOpenChange={onOpenChange} variant="detached" size="default" title={t19} footer={footer} disableDesktopSwipe={true} contentClassName="pt-3 sm:pt-3">{t48}</SilkSheet>;
    $[116] = footer;
    $[117] = onOpenChange;
    $[118] = open;
    $[119] = t19;
    $[120] = t48;
    $[121] = t49;
  } else {
    t49 = $[121];
  }
  return t49;
}